import { useState, useRef, useEffect, useCallback } from 'react';
import moment from 'moment';
import { LocalizationProvider, ptBR, TimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import InputMask from 'react-input-mask';
import * as yup from 'yup';

import IFormError from '../../../../interfaces/IFormError';
import IWizardStep from '../../../../interfaces/IWizardStep';
import WizardButton from '../../components/WizardButton';
import WizardTextField from '../../components/WizardTextField';

import { useValidateSchema } from '../../../../hooks/providers/useValidateSchema';
import { useFocus } from '../../../../hooks/util/useFocus';
import { useWizardRequest } from '../../../../hooks/services/useWizardRequest';
import { TextField } from '@mui/material';
import { useToastrVanila } from '../../../../hooks/util/useToastrVanila';

interface PeriodTimeProps extends IWizardStep {}

const PeriodTime = (props: PeriodTimeProps) => {
  const acrecentoHoras = props.medicalCertificate.typeDocument?.quantityTimeInHoursDefault;
  const { quantityTimeInHoursDefault } = props.medicalCertificate.typeDocument!;

  const [startTime, setStartTime] = useState<moment.Moment | null>(moment(props.medicalCertificate.startTime));
  const [endTime, setEndTime] = useState<moment.Moment | null>(moment(props.medicalCertificate.endTime));
  const [timeQuantity, setTimeQuantity] = useState('');

  const [errors, setErrors] = useState<IFormError | null>(null);
  const [loading, setLoading] = useState(false);

  const [formatForRecordingNumberOfHoursId, setFormatForRecordingNumberOfHoursId] = useState(1);
  const buttonRef = useRef<HTMLInputElement>();

  const request = useWizardRequest();

  const focus = useFocus();
  const validationProvader = useValidateSchema();

  const toastr = useToastrVanila();

  const dateValidation = (date: string, schema: yup.TestContext): yup.ValidationError | boolean => {
    if (!date) {
      return schema.createError({ message: `Campo obrigatório` });
    }
    if (date === 'null') {
      return schema.createError({ message: `Campo obrigatório` });
    }

    if (date === 'Invalid date') {
      return schema.createError({ message: `Data inválida` });
    }
    return true;
  };

  function decimalToTime(decimalStr: string) {
    const decimal = Number(decimalStr.replace(',', '.'));
    const hours = Math.floor(decimal);
    const minutes = Math.round((decimal - hours) * 60);

    const hoursStr = String(hours).padStart(2, '0');
    const minutesStr = String(minutes).padStart(2, '0');

    return `${hoursStr}:${minutesStr}`;
  }

  const schema = yup.object({
    startTime: yup
      .string()
      .nullable(true)
      .test((date, schema) => dateValidation(`${date}`, schema)),
    endTime: yup
      .string()
      .nullable(true)
      .test((date, schema) => {
        if (!endTime?.isAfter(startTime) && !endTime?.isSame(startTime)) {
          return schema.createError({ message: `Deve ser maior ou igual ao início` });
        }

        if (endTime?.isSame(startTime)) {
          return schema.createError({ message: `As horas inicial e final devem ser diferentes` });
        }

        return dateValidation(`${date}`, schema);
      }),
  });

  const handleSubmit = async () => {
    const result = await validationProvader.validate(schema, {
      startTime: startTime?.format() || '',
      endTime: endTime?.format() || '',
    });
    setErrors({ ...result.errors });
    if (!result.isValid) {
      return;
    }
    if (moment.duration(endTime?.diff(startTime)).asHours() >= 23) {
      toastr.error('Informe uma quantidade de horas menor ou igual a 23.');
      return;
    }

    setLoading(true);
    const newData = {
      typeTime: 'HOUR',
      startTime: startTime?.format() || '',
      endTime: endTime?.format() || '',
      timeQuantity: moment.duration(endTime?.diff(startTime)).asHours(),
      startDate: null,
      endDate: null,
      daysQuantity: 0,
    };

    props.setStep(props.step + 1, { ...props.medicalCertificate, ...newData }, props.step);
  };

  const getFormatForRecordingNumberOfHours = async () => {
    await request.getFormatForRecordingNumberOfHours().then(resource => {
      setFormatForRecordingNumberOfHoursId(resource.formatForRecordingNumberOfHoursId);
    });
  };

  const handleChangeTimeQuantity = useCallback(() => {
    if (formatForRecordingNumberOfHoursId === 1) return;

    const [hourRaw, minuteRaw] = String(timeQuantity || '').split(':');

    const hour = hourRaw || '0';
    const minute = minuteRaw && minuteRaw.trim() !== '' ? minuteRaw : '00';

    if (startTime) {
      const time = moment(startTime.format())
        .add(Number(hour), 'hours')
        .add(Number(minute), 'minutes');

      setEndTime(time);
    }
  }, [timeQuantity, startTime]);

  const handleAddDefaultValue = () => {
    if (formatForRecordingNumberOfHoursId === 1) return;
    if (!startTime || acrecentoHoras == null) return;
    const value = acrecentoHoras as string | number;

    const numericHours = Number(value);
    if (!isNaN(numericHours)) {
      const endTime = startTime.clone().add(numericHours, 'hours');
      const limitTimeDay = startTime.hour() + numericHours;
      if (limitTimeDay >= 24) {
        const newEndTime = endTime
          .clone()
          .hour(23)
          .minute(59);
        setEndTime(newEndTime);
        return;
      }
      setEndTime(endTime);
    }
  };

  useEffect(() => {
    getFormatForRecordingNumberOfHours();
  }, []);

  useEffect(() => {
    if (props.medicalCertificate.typeTime === 'DAY') {
      if (quantityTimeInHoursDefault) {
        setTimeQuantity(decimalToTime(String(quantityTimeInHoursDefault)));
      } else {
        setTimeQuantity('');
      }
    }
  }, []);

  useEffect(() => {
    if (props.medicalCertificate.timeQuantity) {
      setTimeQuantity(decimalToTime(String(props.medicalCertificate.timeQuantity)));
    } else if (props.medicalCertificate.typeDocument?.quantityTimeInHoursDefault) {
      setTimeQuantity(decimalToTime(String(quantityTimeInHoursDefault)));
    }
  }, []);

  useEffect(() => {
    handleChangeTimeQuantity();
    handleAddDefaultValue();
  }, [startTime, timeQuantity]);

  return (
    <form>
      <LocalizationProvider
        dateAdapter={AdapterMoment}
        localeText={ptBR.components.MuiLocalizationProvider.defaultProps.localeText}
      >
        <>
          <TimePicker
            value={startTime}
            label="Hora inicial"
            onChange={e => {
              setStartTime(e);
            }}
            renderInput={params => (
              <WizardTextField
                {...params}
                sx={{ marginTop: '20px', width: '300px' }}
                size="small"
                error={Boolean(errors?.startTime)}
                helperText={errors?.startTime}
                placeholder="Selecione o início"
                onBlur={() => handleChangeTimeQuantity()}
              />
            )}
          />
          {formatForRecordingNumberOfHoursId === 1 && (
            <TimePicker
              value={endTime}
              label="Hora final"
              onChange={e => {
                setEndTime(e);
              }}
              minTime={startTime}
              renderInput={params => (
                <WizardTextField
                  {...params}
                  sx={{ marginTop: '30px', width: '300px' }}
                  size="small"
                  error={Boolean(errors?.endTime)}
                  placeholder="Selecione o fim"
                  helperText={errors?.endTime}
                  onBlur={() => handleChangeTimeQuantity()}
                  onKeyUpCapture={e => {
                    focus.setFocus(e, buttonRef);
                  }}
                />
              )}
            />
          )}

          {formatForRecordingNumberOfHoursId === 2 && (
            <>
              <InputMask
                mask={'99:99'}
                value={timeQuantity || ''}
                onChange={e => {
                  setTimeQuantity(e.target.value);
                }}
                onBlur={() => handleChangeTimeQuantity()}
                maskPlaceholder={null}
              >
                <TextField
                  sx={{ marginTop: '30px', width: '300px' }}
                  label="Quantidade de horas"
                  placeholder="00:00"
                  size="small"
                  autoComplete="off"
                />
              </InputMask>

              <TimePicker
                value={endTime}
                label="Hora final"
                onChange={e => {
                  setEndTime(e);
                }}
                minTime={startTime}
                disabled
                renderInput={params => (
                  <WizardTextField
                    {...params}
                    sx={{ marginTop: '30px', width: '300px' }}
                    size="small"
                    error={Boolean(errors?.endTime)}
                    placeholder="Selecione o fim"
                    helperText={errors?.endTime}
                    onKeyUpCapture={e => {
                      focus.setFocus(e, buttonRef);
                    }}
                    disabled
                  />
                )}
              />
            </>
          )}
        </>
      </LocalizationProvider>
      <WizardButton
        isLoading={loading}
        disabled={loading}
        sx={{ marginTop: '100px' }}
        ref={buttonRef}
        onClick={handleSubmit}
      >
        PRÓXIMO
      </WizardButton>
    </form>
  );
};

export default PeriodTime;
